import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { patientApi, type HomeAlert } from '../api';

/**
 * Findings a provider flagged as needing attention now.
 *
 * `GET /home/alerts` has been serving these all along with nothing on the web
 * reading it. This patient has two CRITICAL lab findings on file — a potassium
 * of 6.1 mmol/L flagged for urgent repeat — and no screen in the web app has
 * ever mentioned either.
 *
 * A critical finding is shown at the top of Home, in the provider's own words,
 * with the report it came from. It is not dismissible here: the lab flagged it
 * for a clinician to act on, and hiding it behind a close button on the
 * patient's own screen would be this app deciding it no longer matters.
 */

const SEVERITY: Record<string, { bg: string; border: string; text: string; label: string }> = {
  CRITICAL: { bg: '#FEF2F2', border: '#FCA5A5', text: '#B91C1C', label: 'Critical' },
  HIGH: { bg: '#FFF7ED', border: '#FDBA74', text: '#C2410C', label: 'Needs attention' },
  WARNING: { bg: '#FFFBEB', border: '#FCD34D', text: '#B45309', label: 'Check this' },
};

function fmtWhen(v: string): string {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
}

export default function CriticalAlerts() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<HomeAlert[]>([]);

  useEffect(() => {
    let alive = true;
    patientApi.homeAlerts()
      .then((rows) => { if (alive) setAlerts(Array.isArray(rows) ? rows : []); })
      // A failed load must not invent calm: nothing is shown, and the rest of
      // Home is unaffected.
      .catch(() => { if (alive) setAlerts([]); });
    return () => { alive = false; };
  }, []);

  if (alerts.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
      {alerts.map((a) => {
        const tone = SEVERITY[(a.severity || '').toUpperCase()] ?? SEVERITY.WARNING;
        return (
          <div
            key={a.id}
            style={{
              background: tone.bg, border: `1px solid ${tone.border}`, borderRadius: 14,
              padding: 14, fontFamily: T.font,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{
                fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4, color: T.white,
                background: tone.text, borderRadius: 6, padding: '2px 7px', textTransform: 'uppercase',
              }}>
                {tone.label}
              </span>
              {a.source && (
                <span style={{ fontSize: 10.5, color: tone.text, fontWeight: 600 }}>{a.source}</span>
              )}
            </div>

            <div style={{ fontSize: 13.5, fontWeight: 800, color: tone.text }}>{a.title}</div>
            <div style={{ fontSize: 12, color: T.text, marginTop: 5, lineHeight: 1.55 }}>{a.message}</div>

            <div style={{ fontSize: 10.5, color: T.textSec, marginTop: 8 }}>
              {[a.flaggedBy, a.reference, fmtWhen(a.at)].filter(Boolean).join(' · ')}
            </div>

            {a.route && (
              <button
                onClick={() => navigate(`/patient/${a.route}`)}
                style={{
                  marginTop: 12, padding: '9px 16px', borderRadius: 10, border: 'none',
                  cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: T.font,
                  color: T.white, background: tone.text,
                }}
              >
                Open the report
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
